import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import adminOfferService from "../../services/offerService";

// ===== CRUD THUNKS =====

export const createOfferThunk = createAsyncThunk(
  "offers/create",
  async (offerData, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.createOffer(offerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getOffersThunk = createAsyncThunk(
  "offers/getAll",
  async (filters, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOffers(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getOfferByIdThunk = createAsyncThunk(
  "offers/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOfferById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateOfferThunk = createAsyncThunk(
  "offers/update",
  async ({ id, offerData }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.updateOffer(id, offerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteOfferThunk = createAsyncThunk(
  "offers/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.deleteOffer(id);
      return { ...response.data, deletedId: id };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateOfferStatusThunk = createAsyncThunk(
  "offers/updateStatus",
  async ({ id, isActive, deactivationReason }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.updateOfferStatus(
        id,
        isActive,
        deactivationReason
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== VALIDATION THUNKS =====

export const validateOfferCodeThunk = createAsyncThunk(
  "offers/validateCode",
  async ({ offerCode, excludeId }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.validateOfferCode(
        offerCode,
        excludeId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const testOfferThunk = createAsyncThunk(
  "offers/test",
  async ({ id, testData }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.testOffer(id, testData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== ANALYTICS THUNKS =====

export const getOfferAnalyticsThunk = createAsyncThunk(
  "offers/getAnalytics",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOfferAnalytics(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getOfferUsageThunk = createAsyncThunk(
  "offers/getUsage",
  async ({ id, page, limit, sortBy }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOfferUsage(
        id,
        page,
        limit,
        sortBy
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getCustomerUsageThunk = createAsyncThunk(
  "offers/getCustomerUsage",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getCustomerUsage(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getBranchUsageThunk = createAsyncThunk(
  "offers/getBranchUsage",
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getBranchUsage(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== APPROVAL WORKFLOW THUNKS =====

export const approveOfferThunk = createAsyncThunk(
  "offers/approve",
  async ({ id, comments }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.approveOffer(id, comments);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const rejectOfferThunk = createAsyncThunk(
  "offers/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.rejectOffer(id, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getPendingApprovalsThunk = createAsyncThunk(
  "offers/getPendingApprovals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getPendingApprovals();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== BULK OPERATIONS THUNKS =====

export const bulkCreateOffersThunk = createAsyncThunk(
  "offers/bulkCreate",
  async (offers, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.bulkCreateOffers(offers);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const bulkUpdateOffersThunk = createAsyncThunk(
  "offers/bulkUpdate",
  async (updates, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.bulkUpdateOffers(updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const bulkUpdateStatusThunk = createAsyncThunk(
  "offers/bulkUpdateStatus",
  async ({ offerIds, isActive }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.bulkUpdateStatus(
        offerIds,
        isActive
      );
      return { ...response.data, offerIds, isActive };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const bulkDeleteOffersThunk = createAsyncThunk(
  "offers/bulkDelete",
  async (offerIds, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.bulkDeleteOffers(offerIds);
      return { ...response.data, deletedIds: offerIds };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== TEMPLATE THUNKS =====

export const getOfferTemplatesThunk = createAsyncThunk(
  "offers/getTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOfferTemplates();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createFromTemplateThunk = createAsyncThunk(
  "offers/createFromTemplate",
  async ({ templateId, customizations }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.createFromTemplate(
        templateId,
        customizations
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== IMPORT/EXPORT THUNKS =====

export const importOffersThunk = createAsyncThunk(
  "offers/import",
  async (file, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.importOffers(file);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const exportOffersThunk = createAsyncThunk(
  "offers/export",
  async ({ format, filters }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.exportOffers(format, filters);

      // Create download link for blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `offers-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true, format };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== SCHEDULING THUNKS =====

export const scheduleOfferThunk = createAsyncThunk(
  "offers/schedule",
  async ({ id, activationDate, deactivationDate }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.scheduleOffer(
        id,
        activationDate,
        deactivationDate
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getScheduledOffersThunk = createAsyncThunk(
  "offers/getScheduled",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getScheduledOffers();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== BRANCH-SPECIFIC THUNKS =====

export const getOffersByBranchThunk = createAsyncThunk(
  "offers/getByBranch",
  async ({ branchCode, includeGeneral }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getOffersByBranch(
        branchCode,
        includeGeneral
      );
      return { ...response.data, branchCode };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const cloneOfferThunk = createAsyncThunk(
  "offers/clone",
  async (
    { id, newOfferCode, targetBranches, modifications },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminOfferService.cloneOffer(
        id,
        newOfferCode,
        targetBranches,
        modifications
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== REPORTS THUNKS =====

export const getPerformanceReportThunk = createAsyncThunk(
  "offers/getPerformanceReport",
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getPerformanceReport(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getExpiringOffersThunk = createAsyncThunk(
  "offers/getExpiring",
  async (days, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getExpiringOffers(days);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const getPopularOffersThunk = createAsyncThunk(
  "offers/getPopular",
  async ({ limit, period }, { rejectWithValue }) => {
    try {
      const response = await adminOfferService.getPopularOffers(limit, period);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ===== ACTIONS =====

export const clearOfferError = createAction("offers/clearError");
export const clearOfferValidation = createAction("offers/clearValidation");
export const clearOfferAnalytics = createAction("offers/clearAnalytics");
export const resetOfferState = createAction("offers/resetState");
export const setCurrentOffer = createAction("offers/setCurrent");
export const clearCurrentOffer = createAction("offers/clearCurrent");
